import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { Button } from '~/components/isolated/Button';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { IconButton } from '~/components/isolated/IconButton';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import type { Category } from '~/stores/CategoriesStore';

import { CategoryBlockTitle } from '../shared/CategoryBlockTitle';
import { createCategoryUrl } from '../shared/utils';

interface CategoriesTilesXSScrollableProps {
  category: Category;
  level?: number;
}

const CategoriesTilesXSScrollable = ({
  category,
  level = 2,
}: CategoriesTilesXSScrollableProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { handleClickCategory } = useCategoryAnalytics();

  const [api, setApi] = useState<CarouselApi | undefined>(undefined);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const items =
    level === 3
      ? category.subcategory.flatMap((item) => item.subcategory)
      : category.subcategory;

  if (!items.length || !items[0]?.id) {
    return null;
  }

  const ViewAllComponent = Typography.Text300Semibold;

  const CardTitleComponent = isMobile
    ? Typography.Text100Semibold
    : Typography.Text300Semibold;

  const viewAllCategoryId = category.subcategory[0]?.id;
  const viewAllLinkTo =
    pathname !== '/search' && viewAllCategoryId
      ? createCategoryUrl({ id: viewAllCategoryId })
      : undefined;
  const onViewAllClick = viewAllCategoryId
    ? handleClickCategory(viewAllCategoryId)
    : undefined;

  const showArrows = !isMobile && (!prevBtnDisabled || !nextBtnDisabled);

  const renderTile = (item: Category['subcategory'][number]) => {
    const title = item.previewText || item.name;

    return (
      <Link
        to={createCategoryUrl({ id: item.id })}
        onClick={handleClickCategory(item.id)}
        data-link={`category-${item.id}`}
        className="relative flex size-[110px] shrink-0 overflow-hidden rounded-4 no-underline md:size-[150px] md:rounded-5.5"
      >
        {item.previewImage ? (
          <CloudflareResponseImage
            src={item.previewImage}
            alt={title}
            className="absolute inset-0 size-full object-cover"
            loading="lazy"
            srcSetParams={defaultSrcSetParams}
          />
        ) : (
          <div className="absolute inset-0 bg-surface-transparent-2" />
        )}
        <div className="absolute inset-0 bg-linear-to-b from-black/20 to-black/40" />
        <div className="absolute right-2 bottom-2 left-2 md:right-3 md:bottom-3 md:left-3">
          <CardTitleComponent
            as="span"
            className="wrap-anywhere line-clamp-2 block text-text-primary-inverse!"
          >
            {title}
          </CardTitleComponent>
        </div>
      </Link>
    );
  };

  return (
    <section className="flex w-full flex-col gap-4 md:gap-8">
      <header className="flex w-full items-center gap-3 md:gap-4">
        <CategoryBlockTitle className="wrap-anywhere flex-1">
          {category.name}
        </CategoryBlockTitle>

        <div className="flex shrink-0 items-center gap-4">
          {viewAllLinkTo &&
            (isMobile ? (
              <Link
                to={viewAllLinkTo}
                className="shrink-0 no-underline"
                onClick={onViewAllClick}
              >
                <Button variant="outlined" size="XS">
                  {t('viewAll')}
                </Button>
              </Link>
            ) : (
              <Link
                to={viewAllLinkTo}
                className="cursor-pointer text-text-secondary no-underline transition-colors hover:text-text-primary hover:no-underline"
                onClick={onViewAllClick}
              >
                <ViewAllComponent as="span" className="block">
                  {t('viewAll')}
                </ViewAllComponent>
              </Link>
            ))}

          {showArrows && (
            <div className="flex items-center gap-2">
              <IconButton
                icon="chevron-3.1"
                iconSize={14}
                size="L"
                variant="shadowS"
                disabled={prevBtnDisabled}
                onClick={onPrevButtonClick}
                className="h-12 w-12"
              />
              <IconButton
                icon="chevron-3.1"
                iconSize={14}
                size="L"
                variant="shadowS"
                disabled={nextBtnDisabled}
                onClick={onNextButtonClick}
                className="h-12 w-12 [&>svg]:rotate-180"
              />
            </div>
          )}
        </div>
      </header>

      {!isMobile && (
        <Carousel
          opts={{
            align: 'start',
            slidesToScroll: 1,
            containScroll: 'trimSnaps',
            dragFree: false,
          }}
          setApi={setApi}
          className="w-full"
        >
          <CarouselContent>
            {items.map((item) => (
              <CarouselItem
                key={item.id}
                className="mr-2 basis-auto! pl-0! last:mr-0"
              >
                {renderTile(item)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {isMobile && (
        <div
          className={cn(
            '-mx-4 flex max-w-[calc(100%+32px)] gap-2 overflow-x-auto overflow-y-hidden px-4 pb-2',
            '[-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none]',
            '[&::-webkit-scrollbar]:hidden',
          )}
        >
          {items.map((item) => renderTile(item))}
        </div>
      )}
    </section>
  );
};

export default observer(CategoriesTilesXSScrollable);
